import { InchUtility, PipeFlowDirection } from "./pipe-dataset";
import { createSelector } from "reselect";
import { PIPE_TYPE_SOIL_TIGHT_ID, PIPE_TYPE_SOIL_TIGHT_PERFORATED_ID, PIPE_TYPE_WATER_TIGHT_ID } from "./types";

const pipeNominalDiameter = state => state.pipeGlobals.pipeNominalDiameter;
const pipeFitting = state => state.pipeGlobals.pipeFitting;
const pipeFlowDirection = state => state.pipeGlobals.pipeFlowDirection;
const requiredVolume = state => state.pipeGlobals.pipeRequiredVolume;
const availableWidth = ( state, overrides = {} ) => new InchUtility(( overrides.pipeAvailableWidth || state.pipeGlobals.pipeAvailableWidth ) * 12);
const availableLength = ( state, overrides = {} ) => new InchUtility(( overrides.pipeAvailableLength || state.pipeGlobals.pipeAvailableLength ) * 12);
const stoneAbove = state => new InchUtility(state.pipeGlobals.pipeStoneAbove);
const stoneBelow = state => new InchUtility(state.pipeGlobals.pipeStoneBelow);
const stonePorosity = state => state.pipeGlobals.pipeStonePorosity / 100;
const stonePerimeterOffset = state => new InchUtility(state.pipeGlobals.pipePerimeterOffset);
const area = state => state.pipeBed.area;
const stoneBedVolume = state => state.pipeBed.stoneBedVolume;
const pipeBedVolume = state => state.pipeBed.pipeBedVolume;

const doesPipeFlowDirectionStartOnTop = createSelector(
    pipeFlowDirection,
    pipeFlowDirection => {
        switch (pipeFlowDirection) {
            case PipeFlowDirection.TopLeftToBottomLeft:
            case PipeFlowDirection.TopLeftToBottomRight:
            case PipeFlowDirection.TopRightToBottomLeft:
            case PipeFlowDirection.TopRightToBottomRight:
                return true;
            case PipeFlowDirection.BottomLeftToTopLeft:
            case PipeFlowDirection.BottomLeftToTopRight:
            case PipeFlowDirection.BottomRightToTopLeft:
            case PipeFlowDirection.BottomRightToTopRight:
            default:
                return false;
        }
    },
);

const doesPipeFlowDirectionStartOnBottom = createSelector(
    doesPipeFlowDirectionStartOnTop,
    doesPipeFlowDirectionStartOnTop => !doesPipeFlowDirectionStartOnTop,
);

const doesPipeFlowDirectionEndOnTop = createSelector(
    pipeFlowDirection,
    pipeFlowDirection => {
        switch (pipeFlowDirection) {
            case PipeFlowDirection.BottomLeftToTopLeft:
            case PipeFlowDirection.BottomLeftToTopRight:
            case PipeFlowDirection.BottomRightToTopLeft:
            case PipeFlowDirection.BottomRightToTopRight:
                return true;
            case PipeFlowDirection.TopLeftToBottomLeft:
            case PipeFlowDirection.TopLeftToBottomRight:
            case PipeFlowDirection.TopRightToBottomLeft:
            case PipeFlowDirection.TopRightToBottomRight:
            default:
                return false;
        }
    },
);

const doesPipeFlowDirectionEndOnBottom = createSelector(
    doesPipeFlowDirectionEndOnTop,
    doesPipeFlowDirectionEndOnTop => !doesPipeFlowDirectionEndOnTop,
);

const doesPipeFlowDirectionStartLeft = createSelector(
    pipeFlowDirection,
    pipeFlowDirection => {
        switch (pipeFlowDirection) {
            case PipeFlowDirection.TopLeftToBottomLeft:
            case PipeFlowDirection.TopLeftToBottomRight:
            case PipeFlowDirection.BottomLeftToTopLeft:
            case PipeFlowDirection.BottomLeftToTopRight:
                return true;
            case PipeFlowDirection.TopRightToBottomLeft:
            case PipeFlowDirection.TopRightToBottomRight:
            case PipeFlowDirection.BottomRightToTopLeft:
            case PipeFlowDirection.BottomRightToTopRight:
            default:
                return false;
        }
    },
);

const doesPipeFlowDirectionStartRight = createSelector(
    doesPipeFlowDirectionStartLeft,
    doesPipeFlowDirectionStartLeft => !doesPipeFlowDirectionStartLeft,
);

const doesPipeFlowDirectionEndLeft = createSelector(
    pipeFlowDirection,
    pipeFlowDirection => {
        switch (pipeFlowDirection) {
            case PipeFlowDirection.BottomLeftToTopLeft:
            case PipeFlowDirection.BottomRightToTopLeft:
            case PipeFlowDirection.TopLeftToBottomLeft:
            case PipeFlowDirection.TopRightToBottomLeft:
                return true;
            case PipeFlowDirection.BottomRightToTopRight:
            case PipeFlowDirection.BottomLeftToTopRight:
            case PipeFlowDirection.TopRightToBottomRight:
            case PipeFlowDirection.TopLeftToBottomRight:
            default:
                return false;
        }
    },
);

const doesPipeFlowDirectionEndRight = createSelector(
    doesPipeFlowDirectionEndLeft,
    doesPipeFlowDirectionEndLeft => !doesPipeFlowDirectionEndLeft,
);

const pipeLongLegLength = createSelector(
    pipeNominalDiameter,
    pipeFitting,
    ( pipeNominalDiameter, pipeFitting ) => {
        if ([PIPE_TYPE_SOIL_TIGHT_ID, PIPE_TYPE_SOIL_TIGHT_PERFORATED_ID].some(id => id === pipeFitting.id)) {
            return pipeNominalDiameter.legLengthPlainEnd;
        } else if (pipeFitting.id === PIPE_TYPE_WATER_TIGHT_ID) {
            return new InchUtility(Math.max(pipeNominalDiameter.legLengthGasketEnd.in, pipeNominalDiameter.legLengthWeldedBellEnd.in));
        }

        throw new Error("Pipe fitting is not acceptable");
    },
);

const pipeShortLegLength = createSelector(
    pipeNominalDiameter,
    pipeFitting,
    ( pipeNominalDiameter, pipeFitting ) => {
        if ([PIPE_TYPE_SOIL_TIGHT_ID, PIPE_TYPE_SOIL_TIGHT_PERFORATED_ID].some(id => id === pipeFitting.id)) {
            return pipeNominalDiameter.legLengthPlainEnd;
        } else if (pipeFitting.id === PIPE_TYPE_WATER_TIGHT_ID) {
            return new InchUtility(Math.min(pipeNominalDiameter.legLengthGasketEnd.in, pipeNominalDiameter.legLengthWeldedBellEnd.in));
        }

        throw new Error("Pipe leg length is not acceptable");
    },
);

const pipeBothLegLength = createSelector(
    pipeLongLegLength,
    pipeShortLegLength,
    ( pipeLongLegLength, pipeShortLegLength ) => new InchUtility(pipeLongLegLength.in + pipeShortLegLength.in),
);

const pipeRowSpacing = createSelector(
    pipeLongLegLength,
    pipeShortLegLength,
    ( pipeLongLegLength, pipeShortLegLength ) => new InchUtility(( pipeLongLegLength.in + pipeShortLegLength.in )),
);

const _pipeTotalExcavation = ( pipeNominalDiameter, stoneAbove, stoneBelow, area ) => area * ( pipeNominalDiameter.pipeOD.ft + stoneAbove.ft + stoneBelow.ft );

const pipeTotalExcavation = createSelector(
    pipeNominalDiameter,
    stoneAbove,
    stoneBelow,
    area,
    _pipeTotalExcavation,
);

const _pipeStoneBedAmount = ( pipeTotalExcavation, pipeBedVolume ) => pipeTotalExcavation - pipeBedVolume;

const pipeStoneBedAmount = createSelector(
    pipeTotalExcavation,
    pipeBedVolume,
    _pipeStoneBedAmount,
);

const _pipeStoneBedVolume = ( pipeStoneBedAmount, stonePorosity ) => pipeStoneBedAmount * stonePorosity;

const pipeStoneBedVolume = createSelector(
    pipeStoneBedAmount,
    stonePorosity,
    _pipeStoneBedVolume,
);

const possibleStoneBedMinimumWidth = createSelector(
    pipeNominalDiameter,
    pipeNominalDiameter => {
        switch (pipeNominalDiameter.rawPipeND) {
            case 12:
            case 15:
                return 6;
            case 18:
            case 24:
                return 8;
            case 30:
            case 36:
                return 12;
            case 42:
            case 48:
                return 16;
            case 60:
                return 17;
            default:
                return 0;
        }
    },
);


const calculatedStoneBedMinimumWidth = createSelector(
    pipeNominalDiameter,
    stonePerimeterOffset,
    ( pipeNominalDiameter, stonePerimeterOffset ) => ( stonePerimeterOffset.ft + pipeNominalDiameter.legLengthPlainEnd.ft ) * 2.0 + pipeNominalDiameter.pipeOD.ft,
);

const stoneBedMinimumWidth = createSelector(
    possibleStoneBedMinimumWidth,
    calculatedStoneBedMinimumWidth,
    ( possibleStoneBedMinimumWidth, calculatedStoneBedMinimumWidth ) => Math.max(
        Math.ceil(possibleStoneBedMinimumWidth),
        Math.ceil(calculatedStoneBedMinimumWidth)),
);

const possibleStoneBedMinimumLength = createSelector(
    pipeNominalDiameter,
    pipeNominalDiameter => {
        switch (pipeNominalDiameter.rawPipeND) {
            case 12:
            case 15:
            case 18:
            case 24:
                return 20;
            case 30:
            case 36:
            case 42:
            case 48:
            case 60:
                return 25;
            default:
                return 0;
        }
    },
);

const calculatedStoneBedMinimumLength = createSelector(
    pipeNominalDiameter,
    stonePerimeterOffset,
    ( pipeNominalDiameter, stonePerimeterOffset ) => ( stonePerimeterOffset.ft + pipeNominalDiameter.legLengthPlainEnd.ft ) * 2.0 + pipeNominalDiameter.pipeOD.ft + pipeNominalDiameter.lengthMin.ft,
);

const stoneBedMinimumLength = createSelector(
    possibleStoneBedMinimumLength,
    calculatedStoneBedMinimumLength,
    ( possibleStoneBedMinimumLength, calculatedStoneBedMinimumLength ) => Math.max(Math.ceil(possibleStoneBedMinimumLength), Math.ceil(calculatedStoneBedMinimumLength)),
);

const pipeSelectors = {
    pipeNominalDiameter,
    pipeFitting,
    pipeFlowDirection,
    requiredVolume,
    availableWidth,
    availableLength,
    stoneAbove,
    stoneBelow,
    stonePorosity,
    stonePerimeterOffset,
    doesPipeFlowDirectionStartOnTop,
    doesPipeFlowDirectionStartOnBottom,
    doesPipeFlowDirectionEndOnTop,
    doesPipeFlowDirectionEndOnBottom,
    doesPipeFlowDirectionStartLeft,
    doesPipeFlowDirectionStartRight,
    doesPipeFlowDirectionEndLeft,
    doesPipeFlowDirectionEndRight,
    pipeLongLegLength,
    pipeShortLegLength,
    pipeBothLegLength,
    pipeRowSpacing,
    _pipeTotalExcavation,
    pipeTotalExcavation,
    _pipeStoneBedAmount,
    pipeStoneBedAmount,
    _pipeStoneBedVolume,
    pipeStoneBedVolume,
    stoneBedVolume,
    pipeBedVolume,
    possibleStoneBedMinimumWidth,
    calculatedStoneBedMinimumWidth,
    stoneBedMinimumWidth,
    possibleStoneBedMinimumLength,
    calculatedStoneBedMinimumLength,
    stoneBedMinimumLength,
};

export default pipeSelectors;



// WEBPACK FOOTER //
// ./src/lib/pipe-selectors.js